import { useLiveQuery } from "dexie-react-hooks";
import { Clock, Flag, Folder, ListChecks, Repeat } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { useProject } from "@/hooks/use-tasks";
import { db, type Task } from "@/lib/db";
import { PRIORITY_LABELS } from "@/lib/planner";
import { describeRule } from "@/lib/recurrence";
import { formatDay, formatInZone, formatMinutes } from "@/lib/time";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui";

/** One task: title, due, estimate and tags, with actions on the right. */
export function TaskRow({
	task,
	leading,
	actions,
	showDue = true,
	className,
	...li
}: {
	task: Task;
	leading?: ReactNode;
	actions?: ReactNode;
	showDue?: boolean;
} & Omit<ComponentProps<"li">, "children">) {
	const due = task.due_at
		? formatInZone(new Date(task.due_at), "EEE d MMM, HH:mm")
		: task.due_date
			? formatDay(task.due_date)
			: null;
	const done = task.status === "done";
	const openTask = useUIStore((s) => s.setOpenTaskId);
	const project = useProject(task.project_id);
	const progress = useLiveQuery(async () => {
		const subs = await db.tasks
			.where("parent_id")
			.equals(task.id)
			.filter((t) => !t.deleted_at)
			.toArray();
		return { done: subs.filter((t) => t.status === "done").length, total: subs.length };
	}, [task.id]);

	return (
		<li className={cn("flex min-h-14 items-center gap-2 px-3 py-2", className)} {...li}>
			{leading}
			<div className="min-w-0 flex-1">
				<p className={cn("truncate font-medium", done && "text-muted-foreground line-through")}>
					<button
						type="button"
						className="max-w-full truncate text-left hover:underline focus-visible:underline focus-visible:outline-none"
						onClick={() => openTask(task.id)}
						aria-label={`Open: ${task.title}`}
					>
						{task.title}
					</button>
				</p>
				<div className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
					{showDue && due && <span>{due}</span>}
					{project && (
						<span className="inline-flex items-center gap-0.5">
							<Folder className="size-3" />
							{project.name}
						</span>
					)}
					{!!progress?.total && (
						<span className="inline-flex items-center gap-0.5" aria-label="Subtasks done">
							<ListChecks className="size-3" />
							{progress.done}/{progress.total}
						</span>
					)}
					{task.priority > 0 && (
						<span
							className={cn(
								"inline-flex items-center gap-0.5",
								task.priority === 3 && "text-primary",
							)}
						>
							<Flag className="size-3" />
							{PRIORITY_LABELS[task.priority]}
						</span>
					)}
					{task.recurrence_rule && (
						<span className="inline-flex items-center gap-0.5">
							<Repeat className="size-3" />
							{describeRule(task.recurrence_rule)}
						</span>
					)}
					{task.estimate_min && (
						<span className="inline-flex items-center gap-0.5">
							<Clock className="size-3" />
							{formatMinutes(task.estimate_min)}
						</span>
					)}
					{task.tags.map((tag) => (
						<Badge key={tag} variant="secondary" className="h-5 px-1.5 text-[11px]">
							#{tag}
						</Badge>
					))}
				</div>
			</div>
			{actions && <div className="flex shrink-0 items-center gap-0.5">{actions}</div>}
		</li>
	);
}
